import { pick } from 'lodash-es'
import { useMemo } from 'react'
import { match, P } from 'ts-pattern'
import * as v from 'valibot'
import type { AudioLanguage } from 'src/services/helpers/schema/taskParameters'
import { usePublicDynamicConfig } from 'src/services/restful/dynamicConfig'
import { getNonAsciiStats } from 'src/utils/string'
import type { I2VProFormArgs } from './types'

export type I2vChannelType = 'normal' | 'private'

export type { I2VProFormArgs }
export const EMPTY_SELECT_KEY = 'unset'
/**
 * UI display order for the audio-language dropdown. Constrained via
 * `satisfies` to the canonical {@link AudioLanguage} schema set so removing
 * a value from the schema surfaces here as a compile error — preventing the
 * dropdown from offering options the backend will reject.
 */
export const AUDIO_LANGUAGE_OPTIONS = [
  'none',
  'japanese',
  'english',
  'chinese',
  'korean',
] as const satisfies ReadonlyArray<AudioLanguage>
export type AudioLanguageOption = (typeof AUDIO_LANGUAGE_OPTIONS)[number]

export const DEFAULT_I2VPRO_NEG_PROMPTS =
  'realistic, blur, distortion, disfigurement, low quality, collage, grainy, logos, abstract, warped'

export const DEFAULT_I2VPRO_PARAMS = {
  model: 'v3.0',
  mode: 'professional',
  duration: '5',
  prompts: '',
  negativePrompts: DEFAULT_I2VPRO_NEG_PROMPTS,
  cameraMovement: EMPTY_SELECT_KEY,
  usePromptsHelper: true,
  audioLanguage: 'japanese',
  generateAudio: true,
  refResourceMode: 'firstLastFrames',
  multiRefResource: {
    imageMediaIds: [],
    videoMediaIds: [],
    audioMediaIds: [],
    items: [],
  },
} satisfies I2VProFormArgs

export function parseI2vProTaskSubmitParams(
  modelOrPanelId: string,
  args: I2VProFormArgs & {
    mediaId?: string | null
    tailMediaId?: string | null
  },
): Partial<I2VProFormArgs> {
  const config = getConfigurationFromModelOrPanelId(modelOrPanelId)
  const defaultToPick = ['model', 'mediaId', 'usePromptsHelper']
  const argsToPick: (keyof I2VProFormArgs)[] = []

  if (config.cameraMovement) {
    argsToPick.push('cameraMovement')
  }
  if (config.prompts) {
    argsToPick.push('prompts')
  }
  if (config.negativePrompts) {
    argsToPick.push('negativePrompts')
  }
  if (config.mode?.enable && !config.mode.omitted) {
    argsToPick.push('mode')
  }
  if (config.duration?.enable && !config.duration.omitted) {
    argsToPick.push('duration')
  }
  if (config.tailFrame) {
    argsToPick.push('tailMediaId')
  }
  if (config.audioLanguage) {
    argsToPick.push('generateAudio', 'audioLanguage')
  }

  return pick(args, [...defaultToPick, ...argsToPick])
}

const i2vModelsDynamicSchema = v.looseObject({
  models: v.array(
    v.looseObject({
      model: v.string(),
      panelId: v.string(),
      isNew: v.optional(v.boolean()),
      image: v.optional(v.string()),
      tags: v.optional(v.array(v.string())),
      supportsT2v: v.optional(v.boolean()),
      demoVideo: v.optional(
        v.nullable(
          v.object({
            id: v.optional(v.string()),
            hls: v.optional(v.string()),
          }),
        ),
      ),
      speedTier: v.optional(
        v.union([v.literal(0), v.literal(1), v.literal(2), v.literal(3)]),
      ),
      supportsPrivateMode: v.optional(v.boolean()),
      supportsTailFrame: v.optional(v.boolean()),
      hidden: v.optional(v.boolean()),
      loras: v.optional(
        v.array(
          v.object({
            key: v.string(),
            id: v.string(),
            triggerWords: v.string(),
            backgroundImage: v.string(),
          }),
        ),
      ),
      actionPresets: v.optional(
        v.array(
          v.object({
            key: v.string(),
            label: v.string(),
            id: v.string(),
            type: v.string(),
            backgroundImage: v.string(),
          }),
        ),
      ),
    }),
  ),
  defaultModel: v.optional(v.string()),
})

type ConfigOptionItem<T extends string | number> = {
  enable: boolean
  /** Means this option is cosmetic only;
   *  Will be filtered out before submitting
   */
  omitted?: boolean
  /** Radio options; If provided, will override default options */
  options?: T[]
  /** Can't be modified */
  readonly?: boolean
  /** Default selected */
  default?: T
}
export type MultiReferenceConstraints = {
  maxImages: number
  maxVideos: number
  maxAudios: number
  videoMimes: string[]
  videoExts: string[]
  audioMimes: string[]
  audioExts: string[]
  maxVideoBytes: number
  maxAudioBytes: number
  /** Per-file video duration range in seconds, inclusive. */
  videoDurationSecRange: [number, number]
  /** Max sum of all reference video durations (seconds). */
  videoTotalDurationSec: number
  /** Single-video width / height range in pixels, inclusive. */
  videoDimensionPxRange: [number, number]
  /** Aspect ratio (width / height), inclusive. */
  videoAspectRatioRange: [number, number]
  /** Total pixel count (w × h), inclusive. */
  videoPixelCountRange: [number, number]
  /** FPS range (informational; client does not probe). */
  videoFpsRange: [number, number]
}

/**
 * User-facing limits are intentionally lower than the vendor (Seedance/ByteDance)
 * hard caps (9 / 3 / 3). Two reasons:
 * 1. Vendor guidance discourages saturating the caps (quality degrades).
 * 2. Reserve headroom for internal features that auto-inject additional
 *    references alongside user-supplied ones.
 */
export const V4_MULTI_REFERENCE_CONSTRAINTS: MultiReferenceConstraints = {
  maxImages: 6,
  maxVideos: 2,
  maxAudios: 2,
  videoMimes: ['video/mp4', 'video/quicktime'],
  videoExts: ['.mp4', '.mov'],
  audioMimes: ['audio/wav', 'audio/x-wav', 'audio/wave'],
  audioExts: ['.wav'],
  maxVideoBytes: 50 * 1024 * 1024,
  maxAudioBytes: 15 * 1024 * 1024,
  videoDurationSecRange: [2, 15],
  videoTotalDurationSec: 15,
  videoDimensionPxRange: [300, 6000],
  videoAspectRatioRange: [0.4, 2.5],
  /** Lower ≈ 640×640, upper ≈ 1920×1087 (≈1080p landscape ceiling). */
  videoPixelCountRange: [409600, 2086876],
  videoFpsRange: [24, 60],
}

export type I2vProModelConfig = Partial<{
  cameraMovement: boolean
  prompts: boolean
  negativePrompts: boolean

  mode?: ConfigOptionItem<'basic' | 'professional'>
  duration?: ConfigOptionItem<5 | 10 | 15>
  durationWithTailFrame?: ConfigOptionItem<5 | 10 | 15>
  tailFrame?: boolean
  modesWhenTailFrameNotAvailable?: ('basic' | 'professional')[]
  audioLanguage?: boolean
  multiReference?: MultiReferenceConstraints
}>

/**
 * Accepting both panelId or model name.
 * Prefer model name when one panelId is shared by multiple models
 * (e.g. V4.0 Preview and V4.0 Lite Preview).
 * @param modelOrPanelId
 * @returns
 */
function getConfigurationFromModelOrPanelId(modelOrPanelId: string) {
  return (
    match<string, I2vProModelConfig>(modelOrPanelId)
      // Hailuo 1.0 (aka v2.5)
      .with(P.union('1894d698-c87d-4f87-932c-26af20e78562', 'v2.5'), () => ({
        prompts: true,
        duration: {
          readonly: true,
          enable: true,
          omitted: true,
          options: [5],
          default: 5,
        },
      }))
      // Kling 1.0 (aka v1)
      .with(P.union('79b4c306-bdb8-4700-ab6f-9aaf508b1d39', 'v1'), () => ({
        prompts: true,
        negativePrompts: true,
        duration: {
          enable: true,
        },
        mode: {
          enable: true,
        },
        tailFrame: true,
        modesWhenTailFrameNotAvailable: ['basic'],
      }))
      // Kling 1.5/1.6/2.0 (aka v2/v2.6/v2.7)
      .with(
        P.union('ccdfe579-9433-4a25-b317-77e87f221270', 'v2', 'v2.6', 'v2.7'),
        () => ({
          prompts: true,
          negativePrompts: true,
          cameraMovement: true,
          duration: {
            enable: true,
          },
          mode: {
            enable: true,
            options: ['basic', 'professional'],
            default: 'professional',
          },
          tailFrame: true,
          modesWhenTailFrameNotAvailable: ['basic'],
        }),
      )
      // Framehouse
      .with(P.union('db0b9e06-1c6c-4a94-8e35-a3aa55957556', 'v2.5.1'), () => ({
        prompts: true,
        tailFrame: true,
        duration: {
          enable: true,
          options: [5, 10],
        },
        durationWithTailFrame: {
          enable: true,
          options: [5, 10],
        },
      }))
      // Wan2.2
      .with(P.union('cc2fcd16-1bb0-4f2f-ab29-11960cdaf862', 'v3.0'), () => ({
        prompts: true,
        negativePrompts: true,
        cameraMovement: false,
        duration: {
          readonly: true,
          enable: true,
          omitted: false,
          options: [5],
          default: 5,
        },
        mode: {
          enable: true,
          options: ['basic', 'professional'],
          default: 'professional',
        },
        tailFrame: true,
      }))
      // Seedance (BytePlus)
      .with(P.union('546d06e1-c985-45ad-81ac-04bf75f55230', 'v3.0.1'), () => ({
        prompts: true,
        duration: {
          enable: true,
          options: [5, 10],
        },
        mode: {
          enable: true,
          default: 'professional',
        },
        tailFrame: true,
        durationWithTailFrame: {
          enable: true,
          options: [5, 10],
        },
      }))
      // Seedance Pro 1.5 (BytePlus)
      .with(P.union('15538c7a-e586-4c1a-8b77-9dd44ca49f8e', 'v3.2'), () => ({
        prompts: true,
        duration: {
          enable: true,
          options: [5, 10],
        },
        cameraMovement: false,
        mode: {
          enable: true,
          default: 'professional',
        },
        tailFrame: true,
        durationWithTailFrame: {
          enable: true,
          options: [5, 10],
        },
        audioLanguage: true,
      }))
      /**
       * V4.0 Preview and V4.0 Lite Preview share the same backend
       * panelId, so dispatch must be done by model name only.
       */
      .with('v4.0', () => ({
        prompts: true,
        duration: {
          enable: true,
          options: [5, 10, 15],
        },
        cameraMovement: false,
        mode: {
          enable: true,
          default: 'professional',
        },
        tailFrame: true,
        durationWithTailFrame: {
          enable: true,
          options: [5, 10, 15],
        },
        audioLanguage: true,
        multiReference: V4_MULTI_REFERENCE_CONSTRAINTS,
      }))
      .with('v4.0.1', () => ({
        prompts: true,
        duration: {
          enable: true,
          options: [5, 10, 15],
        },
        mode: {
          enable: true,
          default: 'professional',
        },
        tailFrame: true,
        durationWithTailFrame: {
          enable: true,
          options: [5, 10, 15],
        },
        audioLanguage: true,
        multiReference: V4_MULTI_REFERENCE_CONSTRAINTS,
      }))
      // In-house v3.0.2 / V302 test entry
      .with('019e4e64-8a01-76f8-85a2-bbff4456eefa', 'v3.0.2', () => ({
        prompts: true,
        negativePrompts: false,
        duration: {
          enable: true,
          options: [5, 10],
          default: 5,
        },
        mode: {
          enable: true,
          options: ['basic', 'professional'],
          default: 'professional',
        },
        tailFrame: true,
        durationWithTailFrame: {
          enable: true,
          options: [5, 10],
          default: 5,
        },
      }))
      // Unknown panel, disable all options
      .otherwise(() => ({}))
  )
}

export type I2vProDynamicModelItem = {
  model: string
  panelId: string
  panelConfig: I2vProModelConfig
  isNew?: boolean
  image?: string
  tags?: string[]
  demoVideoId?: string
  demoVideoHlsUrl?: string
  isDefault: boolean
  speedTier?: 0 | 1 | 2 | 3
  supportsPrivateMode: boolean
  supportsTailFrame: boolean
  supportsT2v: boolean
  hiddenByDefault: boolean
  loras?: {
    key: string
    id: string
    triggerWords: string
    backgroundImage: string
  }[]
  actionPresets?: {
    key: string
    label: string
    id: string
    type: string
    backgroundImage: string
  }[]
}

export function useAvaliableI2vModels() {
  const { data, isLoading } = usePublicDynamicConfig('tools')

  const { output, success } = v.safeParse(i2vModelsDynamicSchema, data?.i2vPro)

  const models: I2vProDynamicModelItem[] = useMemo(() => {
    const base: I2vProDynamicModelItem[] = success
      ? output.models.map(
          model =>
            ({
              model: model.model,
              panelId: model.panelId,
              panelConfig: getConfigurationFromModelOrPanelId(model.model),
              isNew: model.isNew,
              image: model.image,
              tags: model.tags,
              demoVideoId: model.demoVideo?.id,
              demoVideoHlsUrl: model.demoVideo?.hls,
              isDefault: model.model === output.defaultModel,
              speedTier: model.speedTier,
              supportsPrivateMode: !!model.supportsPrivateMode,
              supportsTailFrame: !!model.supportsTailFrame,
              supportsT2v: !!model.supportsT2v,

              hiddenByDefault: !!model.hidden,
              loras: model.loras,
              actionPresets: model.actionPresets,
            }) satisfies I2vProDynamicModelItem,
        )
      : []

    return base
  }, [success, output])

  return {
    models,
    defaultModel: success
      ? (output?.defaultModel as I2VProFormArgs['model'])
      : undefined,
    /**
     * Only surface the "initial fetch" loading window — once the config
     * has ever resolved, the models list falls back to cached
     * (or `V4_FALLBACK_MODELS`) so a background refetch shouldn't
     * trigger a full form unmount / LoadingPage flash that would
     * tear down `<VideoCanvas>` and reset the user's pending state.
     */
    isLoading: isLoading && !data,
  }
}

const FRAMEPACK_MODEL_ID = 'v2.5.1'
export function isFramepackModel(model: string) {
  return model === FRAMEPACK_MODEL_ID
}

export function modelSupportsActionControlVideo(model: string) {
  return model === 'v3.0'
}

/**
 * I2V prompt helper only targets prompts that are predominantly English.
 * A prompt is considered non-English (and the helper is suppressed) when
 * BOTH conditions hold:
 * - non-ASCII character ratio exceeds 10% (avoids false-positives on
 *   prompts that sprinkle a few em-dashes / accented chars), AND
 * - at least 5 non-ASCII characters are present (avoids triggering on
 *   short English prompts with 1-2 symbols).
 * Both thresholds were tuned against historical prompt data where the
 * helper produced low-quality rewrites for mixed-language inputs.
 */
const PROMPT_NON_ASCII_RATIO_THRESHOLD = 0.1
const PROMPT_NON_ASCII_COUNT_THRESHOLD = 5
/**
 * These rules are for i2v mode only
 * @param prompts
 * @returns
 */
export function usePromptExceedsAsciiThresholdForI2vMode(
  model: string,
  prompts?: string,
) {
  const canUsePromptsHelper = canUseI2vProPromptHelper(model)
  const stats = useMemo(
    () =>
      prompts
        ? getNonAsciiStats(prompts.replaceAll(/\s/g, ''))
        : { totalCount: 0, nonAsciiCount: 0, nonAsciiRatio: 0 },
    [prompts],
  )
  return (
    canUsePromptsHelper &&
    stats.nonAsciiRatio > PROMPT_NON_ASCII_RATIO_THRESHOLD &&
    stats.nonAsciiCount > PROMPT_NON_ASCII_COUNT_THRESHOLD
  )
}

export function canUseI2vProPromptHelper(model: string, prompts?: string) {
  // v3.2, v4.0, v4.0.1 prompt helper is not reliable yet
  return model !== 'v3.2' && model !== 'v4.0' && model !== 'v4.0.1'
}
